import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators'
import { AAAStore } from '../../store/root-reducer'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { resolveLocalizedMessages } from '../../shared/i18n/i18n.utils'
import { DRR_TOW_DESTINATION_UNAVAILABLE_DEFAULT } from '../../shared/utils/messages'
import { Advisory } from '../advisories/advisories.types'
import { confirmTowDistance } from '../location/tow-location/tow-location.actions'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import events from '../tagging/events'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { openMessageDialog } from '../ui/ui.actions'
import { MessageDialogTypes } from '../ui/ui.types'
import {
  APPLY_TOW_SERVICE_AVAILABILITY,
  TOW_SERVICE_AVAILABILITY,
  applyTowServiceAvailability,
  notifyTowServiceAvailabilityFailure,
  requestTowServiceAvailability,
  towServiceAvailabilitySuccess,
} from './tow-service-availability.actions'
import {
  selectIsTowServiceAvailabilityDeferred,
  selectIsTowServiceAvailabilityLoading,
  selectTowServiceAvailability,
} from './tow-service-availability.selectors'
import { TowServiceAvailabilityService } from './tow-service-availability.service'
import { TowServiceAvailability } from './tow-service-availability.types'

@Injectable()
export class TowServiceAvailabilityEffects {
  constructor(
    private actions$: Actions,
    private errorReportingService: ErrorReportingService,
    private adobeEventService: AdobeEventService,
    private _service: TowServiceAvailabilityService,
    private store$: Store<AAAStore>
  ) {}

  getTowServiceAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestTowServiceAvailability>>(
        TOW_SERVICE_AVAILABILITY.REQUEST
      ),
      switchMap((action) =>
        this._service.getTowServiceAvailability$(action.payload).pipe(
          map((towServiceAvailability) =>
            towServiceAvailabilitySuccess({
              payload: { towServiceAvailability },
            })
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifyTowServiceAvailabilityFailure
            )
          )
        )
      )
    )
  )

  // Resolves the per-destination tow check as soon as the response arrives,
  // unless the request was deferred behind a soft warning: that response stays
  // in state until the user continues (applyTowServiceAvailability$).
  handleTowServiceAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType<
        | ReturnType<typeof towServiceAvailabilitySuccess>
        | ReturnType<typeof notifyTowServiceAvailabilityFailure>
      >(TOW_SERVICE_AVAILABILITY.SUCCESS, TOW_SERVICE_AVAILABILITY.FAILURE),
      withLatestFrom(this.store$.select(selectIsTowServiceAvailabilityDeferred)),
      filter(([, deferred]) => !deferred),
      mergeMap(([action]) =>
        this.resolveAvailabilityActions(
          action.type === TOW_SERVICE_AVAILABILITY.SUCCESS
            ? (action as ReturnType<typeof towServiceAvailabilitySuccess>).payload
              .towServiceAvailability
            : { enabled: true }
        )
      )
    )
  )

  // Continue on a soft warning: resolve the held response. An apply while the
  // request is still in flight is ignored (the Continue button is disabled
  // meanwhile, so this is a guard against failing open past a club block), so
  // a null result here always means the request failed, which is fail-open
  // like an immediate failure.
  applyTowServiceAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof applyTowServiceAvailability>>(
        APPLY_TOW_SERVICE_AVAILABILITY
      ),
      withLatestFrom(
        this.store$.select(selectTowServiceAvailability),
        this.store$.select(selectIsTowServiceAvailabilityLoading)
      ),
      filter(([, , loading]) => !loading),
      mergeMap(([, availability]) =>
        this.resolveAvailabilityActions(availability ?? { enabled: true })
      )
    )
  )

  // The club's advisory is shown whenever one is returned, independently of
  // whether the tow is allowed: when enabled the flow proceeds to passenger
  // selection (confirmTowDistance) and the advisory is informational; when
  // disabled the advisory blocks, and falls back to a generic message if the
  // club returned none. A failed request is fail-open ({ enabled: true }) so a
  // club backend outage never blocks the user.
  private resolveAvailabilityActions(availability: TowServiceAvailability) {
    const messages: Array<Advisory> = resolveLocalizedMessages(
      availability?.advisoryMessages || []
    ).map((msg) => ({ msg }))

    if (availability?.enabled) {
      // Informational only — no call-AAA CTA, since the user can proceed.
      return [
        confirmTowDistance(),
        ...(messages.length
          ? [
            openMessageDialog({
              payload: {
                type: MessageDialogTypes.TOW_RESTRICTION_MESSAGE,
                params: { messages, showCallAaa: false },
              },
            }),
          ]
          : []),
      ]
    }

    this.adobeEventService.sendEvent({
      eventName: AdobeEventTypes.SYSTEM,
      eventValue: events.towTo.DESTINATION_TOW_UNAVAILABLE,
    })

    return [
      openMessageDialog({
        payload: {
          type: MessageDialogTypes.TOW_RESTRICTION_MESSAGE,
          params: {
            messages: messages.length
              ? messages
              : [{ msg: DRR_TOW_DESTINATION_UNAVAILABLE_DEFAULT() }],
          },
        },
      }),
    ]
  }
}
